/**
 * Feature Flags Configuration
 * 
 * Controls visibility of features based on environment (development vs production)
 * Features marked as hidden will only show in development mode
 * 
 * Usage:
 * import { isFeatureEnabled, FEATURES } from './utils/featureFlags';
 * 
 * {isFeatureEnabled(FEATURES.EXPORT_PDF) && <ExportPDFButton />}
 * 
 * Environment Detection:
 * - Uses REACT_APP_ENVCONFIG if set (custom override, e.g. for Railway test deployments)
 * - Falls back to NODE_ENV (automatically set by React: 'development' in dev server, 'production' in build)
 * - To enable dev features on Railway: set REACT_APP_ENVCONFIG=development in Railway environment variables
 */

const currentEnv = process.env.REACT_APP_ENVCONFIG || process.env.NODE_ENV || 'production';
const isDevelopment = currentEnv === 'development';


/**
 * Feature identifiers
 */
export const FEATURES = {
    EXPORT_PDF: 'EXPORT_PDF',
    SOCIAL_SIGNIN: 'SOCIAL_SIGNIN',
    SOCIAL_SIGNUP: 'SOCIAL_SIGNUP',
    FULL_LENGTH_TEST: 'FULL_LENGTH_TEST',
    INVITATION: 'INVITATION',
    SYSTEM_PROMPT_EDITOR: 'SYSTEM_PROMPT_EDITOR',
};

/**
 * Feature configuration
 * Set enabled to true to show in all environments
 * Set enabled to false to only show in development
 * Or use environments array to specify which environments can see the feature
 * Available environments: 'development', 'production'
 */
const featureConfig = {
    [FEATURES.EXPORT_PDF]: {
        name: 'Export PDF Report',
        description: 'Dashboard report export to PDF functionality',
        enabled: false, // Only visible in development
        environments: ['development'],
        location: 'pages/authPages/userDashboardPage.js',
    },
    [FEATURES.SOCIAL_SIGNIN]: {
        name: 'Social Sign In',
        description: 'Social media authentication for sign in (Google, Facebook)',
        enabled: false, // Only visible in development
        environments: ['development'],
        location: 'components/signInForm/signInForm.js',
    },
    [FEATURES.SOCIAL_SIGNUP]: {
        name: 'Social Sign Up',
        description: 'Social media authentication for sign up (Google, Facebook)',
        enabled: false, // Only visible in development
        environments: ['development'],
        location: 'components/signUpForm/signUpForm.js',
    },
    [FEATURES.FULL_LENGTH_TEST]: {
        name: 'Full Length SAT Test',
        description: 'Full length SAT practice test in SAT practice page',
        enabled: false, // Only visible in development
        environments: ['development'],
        location: 'pages/authPages/userSATPracticePage.js',
    },
    [FEATURES.INVITATION]: {
        name: 'Invitation System',
        description: 'Email invitation and referral link sharing on settings page',
        enabled: false, // Only visible in development
        environments: ['development'],
        location: 'pages/authPages/userSetting.js',
    },
    [FEATURES.SYSTEM_PROMPT_EDITOR]: {
        name: 'Practice Module Tool',
        description: 'Admin toolbox for system prompt editing and question ID navigation in practice mode',
        enabled: false, // Only visible in development
        environments: ['development'],
        location: 'components/examinationContent/ChatBot/ChatBot.js',
    },
};

/**
 * Check if a feature is enabled
 * @param {string} featureKey - Feature identifier from FEATURES object
 * @returns {boolean} - True if feature should be visible
 */
export const isFeatureEnabled = (featureKey) => {
    const feature = featureConfig[featureKey];

    if (!feature) {
        console.warn(`Feature flag "${featureKey}" not found in configuration`);
        return false;
    }

    // Check for localStorage override (admin feature)
    try {
        const overrides = localStorage.getItem('featureFlagOverrides');
        if (overrides) {
            const parsedOverrides = JSON.parse(overrides);
            if (parsedOverrides[featureKey] !== undefined) {
                return parsedOverrides[featureKey];
            }
        }
    } catch (e) {
        console.error('Failed to parse feature flag overrides', e);
    }

    // If feature is explicitly enabled, show in all environments
    if (feature.enabled) {
        return true;
    }

    // Check if current environment is in the allowed environments list
    if (feature.environments && Array.isArray(feature.environments)) {
        return feature.environments.includes(currentEnv);
    }

    // Fallback: If feature is disabled and no environments specified, only show in development
    return isDevelopment;
};

/**
 * Get all feature flags and their current status
 * @returns {Object} - Object with feature keys and their enabled status
 */
export const getAllFeatureFlags = () => {
    const flags = {};

    Object.keys(featureConfig).forEach(key => {
        flags[key] = {
            ...featureConfig[key],
            isEnabled: isFeatureEnabled(key),
            currentEnvironment: currentEnv,
        };
    });

    return flags;
};

/**
 * Get list of hidden features (only visible in development)
 * @returns {Array} - Array of feature objects that are currently hidden in production
 */
export const getHiddenFeatures = () => {
    return Object.keys(featureConfig)
        .filter(key => !featureConfig[key].enabled)
        .map(key => ({
            key,
            ...featureConfig[key],
            isEnabled: isFeatureEnabled(key),
        }));
};

/**
 * Get list of enabled features (visible in all environments)
 * @returns {Array} - Array of feature objects that are enabled in production
 */
export const getEnabledFeatures = () => {
    return Object.keys(featureConfig)
        .filter(key => featureConfig[key].enabled)
        .map(key => ({
            key,
            ...featureConfig[key],
            isEnabled: isFeatureEnabled(key),
        }));
};

/**
 * Log feature flags status (useful for debugging)
 */
export const logFeatureFlags = () => {
    console.group('🚩 Feature Flags Status');
    console.log(`Environment: ${currentEnv} (${isDevelopment ? 'Development' : 'Production'})`);
    console.log('---');

    Object.keys(featureConfig).forEach(key => {
        const feature = featureConfig[key];
        const enabled = isFeatureEnabled(key);
        const allowedEnvs = feature.environments ? feature.environments.join(', ') : 'development only';
        console.log(
            `${enabled ? '✅' : '❌'} ${feature.name}`,
            `\n   Location: ${feature.location}`,
            `\n   Status: ${feature.enabled ? 'Enabled (all envs)' : `Limited to: ${allowedEnvs}`}`,
            `\n   Visible: ${enabled ? 'Yes' : 'No'}`
        );
    });

    console.groupEnd();
};

/**
 * Get current environment
 * @returns {string} - Current environment (development, production)
 */
export const getCurrentEnvironment = () => currentEnv;

/**
 * Check if running in specific environment
 * @param {string} env - Environment to check ('development', 'production')
 * @returns {boolean}
 */
export const isEnvironment = (env) => currentEnv === env;

const featureFlagsModule = {
    FEATURES,
    isFeatureEnabled,
    getAllFeatureFlags,
    getHiddenFeatures,
    getEnabledFeatures,
    logFeatureFlags,
    getCurrentEnvironment,
    isEnvironment,
};

export default featureFlagsModule;
